import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';
import logoUrl from '../components/midone/assets/images/logo.svg';
import illustrationUrl from '../components/midone/assets/images/illustration.svg';
import { FormInput, FormCheck } from '../components/midone/components/Base/Form';
import Button from '../components/midone/components/Base/Button';

import { useNavigate } from 'react-router-dom';
import { login, fetchCurrentUser } from '../api/auth';
import { clearAuthToken, getAuthToken, setAuthToken } from '../utils/auth';

const Login: FC = () => {
  
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const appName = process.env.REACT_APP_NAME || 'Workshop';
  const logoSrc = process.env.REACT_APP_LOGO || logoUrl;
  const faviconHref = process.env.REACT_APP_FAVICON || '/favicon.ico';

  useEffect(() => {
    document.title = `${appName} – Login`;

    const favicon = document.querySelector("link[rel='icon']") as
      | HTMLLinkElement
      | null;

    if (favicon) {
      favicon.href = faviconHref;
    }
  }, [appName, faviconHref]);

  useEffect(() => {
    const existingToken = getAuthToken();

    if (!existingToken) {
      return;
    }

    let isMounted = true;

    fetchCurrentUser()
      .then(() => {
        if (isMounted) {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => {
        clearAuthToken();
      });

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setToastMessage(null);

    try {
      const response = await login({
        username: username.trim(),
        password,
      });

      const role = (response as any).role ?? response.user.typeKey ?? null;
      const adminLike = role === 'ADMIN' || role === 'DIRECTOR';
      const allowed = adminLike || role === 'ADVISOR' || role === 'STORE_MANAGER';

      if (!allowed) {
        clearAuthToken();
        setErrorMessage('Not authorized for this user role. Only ADMIN, DIRECTOR, ADVISOR, or STORE_MANAGER roles are allowed.');
        return;
      }

      const parsedExpires = Number(response.expiresIn);
      const apiSuggestedExpiry = Number.isFinite(parsedExpires)
        ? parsedExpires
        : 60 * 60;
      const expiresInSeconds = remember ? 60 * 60 * 24 * 30 : apiSuggestedExpiry;
      setAuthToken(response.accessToken, expiresInSeconds);
      // role is allowed; proceed

      setToastMessage('Login Successful');
      setTimeout(() => navigate('/dashboard', { replace: true }), 800);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message || 'Failed to login. Please try again.'
          : 'Failed to login. Please try again.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    setter: (value: string) => void,
  ) =>
    (event: ChangeEvent<HTMLInputElement>) => setter(event.target.value);

  return (
    <div
      className="p-3 sm:px-8 relative h-screen lg:overflow-hidden bg-primary xl:bg-white dark:bg-darkmode-800 xl:dark:bg-darkmode-600 before:hidden before:xl:block before:content-[''] before:w-[57%] before:-mt-[28%] before:-mb-[16%] before:-ml-[13%] before:absolute before:inset-y-0 before:left-0 before:transform before:rotate-[-4.5deg] before:bg-primary/20 before:rounded-[100%] before:dark:bg-darkmode-400 after:hidden after:xl:block after:content-[''] after:w-[57%] after:-mt-[20%] after:-mb-[13%] after:-ml-[13%] after:absolute after:inset-y-0 after:left-0 after:transform after:rotate-[-4.5deg] after:bg-primary after:rounded-[100%] after:dark:bg-darkmode-700"
    >
      {toastMessage && (
        <div className="absolute inset-x-0 top-4 flex justify-center">
          <div className="bg-emerald-500 text-white px-4 py-2 rounded shadow">
            {toastMessage}
          </div>
        </div>
      )}
      <div className="container relative z-10 sm:px-10">
        <div className="block grid-cols-2 gap-4 xl:grid">
          <div className="flex-col hidden min-h-screen xl:flex">
            <a href="" className="flex items-center pt-5 -intro-x">
              <img
                alt="Midone Tailwind HTML Admin Template"
                className="w-6"
                src={logoSrc}
              />
              <span className="ml-3 text-lg text-white"> {appName} </span>
            </a>
            <div className="my-auto">
              <img
                alt="Midone Tailwind HTML Admin Template"
                className="w-1/2 -mt-16 -intro-x"
                src={illustrationUrl}
              />
              <div className="mt-10 text-4xl font-medium leading-tight text-white -intro-x">
                Powering Retail Excellence
              </div>
              <div className="mt-5 text-lg text-white -intro-x text-opacity-70 dark:text-slate-400">
                Effortlessly streamline your retail operations with a single access
              </div>
            </div>
          </div>
          <div className="flex h-screen py-5 my-10 xl:h-auto xl:py-0 xl:my-0">
            <div className="w-full px-5 py-8 mx-auto my-auto bg-white rounded-md shadow-md xl:ml-20 dark:bg-darkmode-600 xl:bg-transparent sm:px-8 xl:p-0 xl:shadow-none sm:w-3/4 lg:w-2/4 xl:w-auto">
              <h2 className="text-2xl font-bold text-center intro-x xl:text-3xl xl:text-left">
                Sign In
              </h2>
              <div className="mt-2 text-center intro-x text-slate-400 xl:hidden">
                Powering Retail Excellence. Effortlessly
                streamline your retail operations with a single access
              </div>
              <form className="mt-8 intro-x" onSubmit={handleSubmit}>
                {errorMessage && (
                  <div className="mb-4 rounded border border-danger/50 bg-danger/10 px-4 py-2 text-danger">
                    {errorMessage}
                  </div>
                )}
                <FormInput
                  type="text"
                  className="block px-4 py-3 intro-x min-w-full xl:min-w-[350px]"
                  placeholder="Username"
                  value={username}
                  onChange={handleInputChange(setUsername)}
                  required
                />
                <FormInput
                  type="password"
                  className="block px-4 py-3 mt-4 intro-x min-w-full xl:min-w-[350px]"
                  placeholder="Password"
                  value={password}
                  onChange={handleInputChange(setPassword)}
                  required
                />
                <div className="flex mt-4 text-xs intro-x text-slate-600 dark:text-slate-500 sm:text-sm">
                  <div className="flex items-center mr-auto">
                    <FormCheck.Input
                      id="remember-me"
                      type="checkbox"
                      className="mr-2 border"
                      checked={remember}
                      onChange={(event) => setRemember(event.target.checked)}
                    />
                    <label className="cursor-pointer select-none" htmlFor="remember-me">
                      Remember me
                    </label>
                  </div>
                </div>
                <div className="mt-5 text-center intro-x xl:mt-8 xl:text-left">
                  <Button
                    variant="primary"
                    className="w-full px-4 py-3 align-top xl:w-32"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Signing in...' : 'Login'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
